import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { NotebookPen, Eye, EyeOff, Pencil, Trash2, Plus, Loader2, ShieldCheck } from "lucide-react";

export const AGENDA_CATS = { nota: "Nota", spid: "SPID", cie: "CIE", credenziali: "Credenziali", altro: "Altro" };
const EMPTY = { title: "", category: "nota", content: "" };

const AgendaItem = ({ e, onEdit, onDelete }) => {
  const [show, setShow] = useState(e.category === "nota");
  return (
    <div className="paper-card p-4" data-testid={`agenda-entry-${e.id}`}>
      <div className="flex items-start gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest bg-labor-olive text-white px-2 py-0.5">{AGENDA_CATS[e.category]}</span>
        <div className="font-semibold flex-1 min-w-0 break-words">{e.title}</div>
        <button data-testid={`agenda-toggle-${e.id}`} onClick={() => setShow(!show)} className="p-1 text-labor-muted hover:text-labor-ink" title={show ? "Nascondi" : "Mostra"}>
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
        <button data-testid={`agenda-edit-${e.id}`} onClick={() => onEdit(e)} className="p-1 text-labor-olive hover:bg-labor-skyBg"><Pencil className="w-4 h-4" /></button>
        <button data-testid={`agenda-delete-${e.id}`} onClick={() => onDelete(e.id)} className="p-1 text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="mt-2 text-sm whitespace-pre-wrap break-words font-mono bg-labor-bg p-2" data-testid={`agenda-content-${e.id}`}>
        {show ? e.content : "••••••••••"}
      </div>
      <div className="text-[10px] text-labor-muted mt-1">Aggiornata il {new Date(e.updated_at).toLocaleString("it-IT")}</div>
    </div>
  );
};

export const AgendaPanel = () => {
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/user/agenda").then((r) => setEntries(r.data.entries || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) { alert("Inserisci titolo e contenuto"); return; }
    setSaving(true);
    try {
      if (editId) await api.patch(`/user/agenda/${editId}`, form);
      else await api.post("/user/agenda", form);
      setForm(EMPTY); setEditId(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa nota?")) return;
    await api.delete(`/user/agenda/${id}`); load();
  };

  return (
    <div data-testid="agenda-panel">
      <div className="paper-card p-6 mb-4">
        <div className="flex items-center gap-2 mb-2"><NotebookPen className="w-5 h-5 text-labor-terracotta" /><h2 className="font-display text-2xl">Agenda Personale</h2></div>
        <p className="text-sm text-labor-muted mb-4 flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 text-labor-italianGreen flex-shrink-0 mt-0.5" />
          Salva appunti e credenziali (SPID, CIE, PIN…). I dati sono cifrati e visibili solo a te e agli operatori CAF Labor, che possono consultarli per aiutarti.
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          <input data-testid="agenda-title" placeholder="Titolo (es. SPID Poste)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="sm:col-span-2 px-3 py-2.5 border border-labor-border" />
          <select data-testid="agenda-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="px-3 py-2.5 border border-labor-border bg-white">
            {Object.entries(AGENDA_CATS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <textarea data-testid="agenda-content" rows={3} placeholder="Contenuto (es. utente, password, PIN, scadenze…)" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="sm:col-span-3 px-3 py-2.5 border border-labor-border" />
        </div>
        <div className="flex gap-2 mt-3">
          <button data-testid="agenda-save" onClick={save} disabled={saving} className="olive-btn px-5 py-2.5 text-sm font-semibold flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {editId ? "Salva modifiche" : "Aggiungi nota"}
          </button>
          {editId && <button data-testid="agenda-cancel-edit" onClick={() => { setEditId(null); setForm(EMPTY); }} className="text-sm text-labor-muted underline">Annulla</button>}
        </div>
      </div>
      {entries.length === 0 ? (
        <div className="paper-card p-6 text-sm text-labor-muted" data-testid="agenda-empty">Nessuna nota salvata.</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {entries.map((e) => (
            <AgendaItem key={e.id} e={e} onDelete={remove}
              onEdit={(x) => { setEditId(x.id); setForm({ title: x.title, category: x.category, content: x.content }); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
          ))}
        </div>
      )}
    </div>
  );
};
